import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import CbmContext from '../Context/Cbm/cbmContext';
import usePermissions from '../hooks/usePermissions';
import { features } from '../config/features';
import {
  downloadBinary,
  pdfUrl,
  requestOtp,
  uploadsZipUrl,
  verifyOtp,
} from '../Services/securePackageService';
import { describeSecurePackageError } from '../Services/securePackageErrors';
import {
  securePackageCopy,
  securePackageFileLabels,
} from '../copy/securePackage';
import {
  getCorrelationIdFromError,
  getCorrelationIdFromResponse,
  trackSecurePackageEvent,
} from '../Services/telemetryService';
import './SecurePackage.css';

const normalizeFormType = (value) => {
  const slug = String(value || '').toLowerCase();
  if (slug === 'newhire') return 'new-hire';
  return slug;
};

const getDisplayFormName = (formType) => {
  if (!formType) return 'Secure Package';
  if (formType === 'new-hire') return 'New Hire';
  return formType
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const toTimestamp = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return value;
  if (value instanceof Date) return value.getTime();
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const formatRemaining = (milliseconds) => {
  const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes > 0) {
    return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
  }
  return `${seconds}s`;
};

const SecurePackage = () => {
  const { formType, formToken } = useParams();
  const cbmContext = useContext(CbmContext);
  const { authLoading } = cbmContext;
  const { portalUser, isAdmin, hasOfficeAccess } = usePermissions();
  const featureEnabled = features.securePackagePages !== false;
  const mountLoggedRef = useRef(false);

  const normalizedFormType = useMemo(
    () => normalizeFormType(formType),
    [formType]
  );
  const displayFormName = useMemo(
    () => getDisplayFormName(normalizedFormType),
    [normalizedFormType]
  );
  const filenameBase = useMemo(
    () =>
      displayFormName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'document',
    [displayFormName]
  );
  const isNewHirePackage = normalizedFormType === 'new-hire';
  const isAllowed = isAdmin || hasOfficeAccess;

  useEffect(() => {
    if (mountLoggedRef.current) {
      return;
    }

    mountLoggedRef.current = true;
    trackSecurePackageEvent('secure_package_page_mount', {
      formType: normalizedFormType,
      pageVariant: isNewHirePackage ? 'new-hire' : 'standard',
      featureEnabled,
      accessState: !featureEnabled
        ? 'disabled'
        : authLoading || !portalUser
        ? 'loading'
        : isAllowed
        ? 'allowed'
        : 'denied',
    });
  }, [
    authLoading,
    featureEnabled,
    isAllowed,
    isNewHirePackage,
    normalizedFormType,
    portalUser,
  ]);

  useEffect(() => {
    if (!featureEnabled) {
      trackSecurePackageEvent('secure_package_feature_disabled', {
        formType: normalizedFormType,
      });
    }
  }, [featureEnabled, normalizedFormType]);

  const [activeAction, setActiveAction] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [otpDestination, setOtpDestination] = useState(null);
  const [otpStage, setOtpStage] = useState('idle');
  const [otpCode, setOtpCode] = useState('');
  const [grantedUntil, setGrantedUntil] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [requestedCategory, setRequestedCategory] = useState(null);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const grantRemaining = useMemo(() => {
    if (!grantedUntil) return 0;
    return grantedUntil - now;
  }, [grantedUntil, now]);

  const hasGrant = grantRemaining > 0;

  useEffect(() => {
    if (grantedUntil && grantRemaining <= 0 && otpStage === 'granted') {
      setGrantedUntil(null);
      setOtpStage('idle');
      setOtpDestination(null);
      setNotice(
        'Your verification window expired. Request a new code to continue.'
      );
    }
  }, [grantRemaining, grantedUntil, otpStage]);

  useEffect(() => {
    if (hasGrant) {
      setNotice(
        `Restricted items are unlocked for ${formatRemaining(grantRemaining)}.`
      );
    }
  }, [hasGrant, grantRemaining]);

  const handleError = useCallback((err) => {
    const mapped = describeSecurePackageError(err);
    setError(mapped);
    setNotice(null);
    return mapped;
  }, []);

  const logOperationFailure = useCallback(
    (operation, err, extra = {}) => {
      const mapped = handleError(err);
      trackSecurePackageEvent(`${operation}_failure`, {
        formType: normalizedFormType,
        errorClass: mapped.errorClass,
        correlationId:
          mapped.correlationId || getCorrelationIdFromError(err) || null,
        status: err?.response?.status || null,
        retryable: Boolean(mapped.retryable),
        ...extra,
      });
      return mapped;
    },
    [handleError, normalizedFormType]
  );

  const logOperationSuccess = useCallback(
    (operation, response, extra = {}) => {
      const correlationId =
        response?.correlationId || getCorrelationIdFromResponse(response);
      trackSecurePackageEvent(`${operation}_success`, {
        formType: normalizedFormType,
        correlationId,
        status: response?.status || 200,
        ...extra,
      });
    },
    [normalizedFormType]
  );

  const requestOtpChallenge = useCallback(
    async (category = null) => {
      if (!isNewHirePackage) return;

      setActiveAction('otp-request');
      setError(null);
      setNotice(null);
      setRequestedCategory(category);

      trackSecurePackageEvent('secure_package_otp_request_start', {
        formType: normalizedFormType,
        requestedCategory: category || null,
      });

      try {
        const response = await requestOtp(normalizedFormType, formToken);
        setOtpDestination(
          response?.destination || response?.maskedDestination || null
        );
        setOtpStage('awaitingCode');
        if (response?.message) {
          setNotice(response.message);
        }
        trackSecurePackageEvent('secure_package_otp_request_success', {
          formType: normalizedFormType,
          requestedCategory: category || null,
          correlationId: response?.correlationId || null,
        });
      } catch (err) {
        logOperationFailure('secure_package_otp_request', err, {
          requestedCategory: category || null,
        });
      } finally {
        setActiveAction(null);
      }
    },
    [formToken, isNewHirePackage, logOperationFailure, normalizedFormType]
  );

  const verifyOtpChallenge = useCallback(async () => {
    if (otpCode.trim().length !== 6) {
      return;
    }

    setActiveAction('otp-verify');
    setError(null);
    setNotice(null);

    trackSecurePackageEvent('secure_package_otp_verify_start', {
      formType: normalizedFormType,
    });

    try {
      const response = await verifyOtp(
        normalizedFormType,
        formToken,
        otpCode.trim()
      );
      const grantedUntilValue = toTimestamp(
        response?.grantedUntil || response?.granted_until
      );
      setGrantedUntil(grantedUntilValue);
      setOtpStage('granted');
      setOtpCode('');
      if (response?.message) {
        setNotice(response.message);
      } else if (grantedUntilValue) {
        setNotice(
          `Verification complete. Restricted files are unlocked until ${new Date(
            grantedUntilValue
          ).toLocaleTimeString()}.`
        );
      }
      trackSecurePackageEvent('secure_package_otp_verify_success', {
        formType: normalizedFormType,
        correlationId: response?.correlationId || null,
        grantDurationMs:
          grantedUntilValue && Number.isFinite(grantedUntilValue)
            ? Math.max(0, grantedUntilValue - Date.now())
            : null,
      });
    } catch (err) {
      logOperationFailure('secure_package_otp_verify', err);
    } finally {
      setActiveAction(null);
    }
  }, [formToken, logOperationFailure, normalizedFormType, otpCode]);

  const handleDownloadPdf = useCallback(async () => {
    setActiveAction('download-pdf');
    setError(null);
    setNotice(null);

    trackSecurePackageEvent('secure_package_download_attempt', {
      formType: normalizedFormType,
      downloadType: 'pdf',
    });

    try {
      const response = await downloadBinary(
        pdfUrl(normalizedFormType, formToken),
        `${filenameBase}.pdf`
      );
      logOperationSuccess('secure_package_download', response, {
        downloadType: 'pdf',
      });
    } catch (err) {
      logOperationFailure('secure_package_download', err, {
        downloadType: 'pdf',
      });
    } finally {
      setActiveAction(null);
    }
  }, [
    filenameBase,
    formToken,
    logOperationFailure,
    logOperationSuccess,
    normalizedFormType,
  ]);

  const handleDownloadUploads = useCallback(async () => {
    setActiveAction('download-zip');
    setError(null);
    setNotice(null);

    trackSecurePackageEvent('secure_package_download_attempt', {
      formType: normalizedFormType,
      downloadType: 'uploads-zip',
    });

    try {
      const response = await downloadBinary(
        uploadsZipUrl(normalizedFormType, formToken),
        `${filenameBase}-uploads.zip`
      );
      logOperationSuccess('secure_package_download', response, {
        downloadType: 'uploads-zip',
      });
    } catch (err) {
      logOperationFailure('secure_package_download', err, {
        downloadType: 'uploads-zip',
      });
    } finally {
      setActiveAction(null);
    }
  }, [
    filenameBase,
    formToken,
    logOperationFailure,
    logOperationSuccess,
    normalizedFormType,
  ]);

  if (!featureEnabled) {
    return (
      <div className="secure-package-page">
        <div className="secure-package-shell">
          <section className="secure-package-card secure-package-denied-card">
            <div className="secure-package-kicker">Temporarily unavailable</div>
            <h1>Secure package pages are currently disabled.</h1>
            <p>
              The admin table still works. If you need a document, use the admin
              forms view until the feature is re-enabled.
            </p>
          </section>
        </div>
      </div>
    );
  }

  if (authLoading || !portalUser) {
    return (
      <div className="secure-package-page">
        <div className="secure-package-shell">
          <section className="secure-package-card secure-package-loading-card">
            <div className="secure-package-spinner" aria-hidden="true" />
            <h1>Loading secure package...</h1>
            <p>Checking your signed-in session and package access.</p>
          </section>
        </div>
      </div>
    );
  }

  if (!isAllowed) {
    return (
      <div className="secure-package-page">
        <div className="secure-package-shell">
          <section className="secure-package-card secure-package-denied-card">
            <div className="secure-package-kicker">Access denied</div>
            <h1>Only Admin and Office users can open secure package links.</h1>
            <p>
              This link is protected because it may expose a PDF, uploads, or
              sensitive identity files.
            </p>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="secure-package-page">
      <div className="secure-package-shell">
        <section className="secure-package-hero">
          <div className="secure-package-hero-copy">
            <div className="secure-package-kicker">Secure package</div>
            <h1>{displayFormName}</h1>
            <p>{securePackageCopy.banner}</p>
            <p>{securePackageCopy.expiryHint}</p>
          </div>
          <div className="secure-package-meta">
            <span className="secure-pill">Admin / Office</span>
            <span className="secure-pill">Tokenized link</span>
            <span className="secure-pill">No attachments</span>
          </div>
        </section>

        {error && (
          <section className="secure-alert secure-alert-error">
            <div className="secure-alert-title">{error.title}</div>
            <p>{error.message}</p>
            <p>Code: {error.errorClass}</p>
            {error.correlationId && (
              <small>Reference: {error.correlationId}</small>
            )}
          </section>
        )}

        {notice && (
          <section className="secure-alert secure-alert-info">
            <p>{notice}</p>
          </section>
        )}

        <section className="secure-package-card">
          <div className="secure-card-header">
            <div>
              <h2>Download PDF</h2>
              <p>
                Open the current submission PDF directly from the secure link.
              </p>
            </div>
            <button
              type="button"
              className="secure-button"
              onClick={handleDownloadPdf}
              disabled={Boolean(activeAction)}
            >
              {activeAction === 'download-pdf'
                ? 'Downloading...'
                : 'Download PDF'}
            </button>
          </div>
        </section>

        {isNewHirePackage && (
          <>
            <section className="secure-package-card">
              <div className="secure-card-header">
                <div>
                  <h2>Package download</h2>
                  <p>
                    Use the ZIP for the full New Hire package. Individual
                    attachments are no longer shown here.
                  </p>
                </div>
                <button
                  type="button"
                  className="secure-button secondary"
                  onClick={handleDownloadUploads}
                  disabled={Boolean(activeAction)}
                >
                  {activeAction === 'download-zip'
                    ? 'Downloading...'
                    : 'Download Uploads (ZIP)'}
                </button>
              </div>
            </section>

            <section className="secure-package-card secure-otp-card">
              <div className="secure-card-header">
                <div>
                  <h2>One-time verification</h2>
                  <p>{securePackageCopy.stepUpExplain}</p>
                </div>
                <button
                  type="button"
                  className="secure-button secondary"
                  onClick={() => requestOtpChallenge(requestedCategory)}
                  disabled={Boolean(activeAction)}
                >
                  {activeAction === 'otp-request'
                    ? 'Requesting...'
                    : 'Request Code'}
                </button>
              </div>

              <div className="secure-otp-status">
                {otpDestination ? (
                  <p>
                    Code sent to <strong>{otpDestination}</strong>
                  </p>
                ) : (
                  <p>
                    If you need to open a restricted file directly, request a
                    code here.
                  </p>
                )}
                {requestedCategory && (
                  <p>
                    Requested item:{' '}
                    <strong>
                      {securePackageFileLabels[requestedCategory]}
                    </strong>
                  </p>
                )}
                {hasGrant && (
                  <p className="secure-otp-success">
                    Verification active for {formatRemaining(grantRemaining)}.
                  </p>
                )}
              </div>

              <div className="secure-otp-input-row">
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={otpCode}
                  onChange={(e) =>
                    setOtpCode(e.target.value.replace(/\D/g, ''))
                  }
                  placeholder="Enter 6-digit code"
                  aria-label="Verification code"
                />
                <button
                  type="button"
                  className="secure-button"
                  onClick={verifyOtpChallenge}
                  disabled={
                    otpCode.trim().length !== 6 || activeAction === 'otp-verify'
                  }
                >
                  {activeAction === 'otp-verify'
                    ? 'Verifying...'
                    : 'Verify Code'}
                </button>
              </div>

              <p className="secure-package-note">
                {securePackageCopy.fallbackOnly}
              </p>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default SecurePackage;
